export interface FinanceMilestoneFilterOption {
  label: string
  value: string
}

export interface ProjectTypeFilterOption {
  label: string
  value: string
  milestones?: FinanceMilestoneFilterOption[]
}

// With no project type selected the milestone picker offers every milestone
// across every project type on this project; with one or more selected it
// narrows to just theirs. Either way, a milestone shared by more than one
// project type (e.g. a "Permit" step common to Kitchen and Bathroom) must
// appear once, not once per type — dedupe by id in first-seen order.
export const deriveMilestoneOptions = (
  projectTypeOptions: ProjectTypeFilterOption[] | undefined,
  selectedProjectTypeIds: string[] | null | undefined
): FinanceMilestoneFilterOption[] => {
  const types = projectTypeOptions ?? []
  const relevantTypes = selectedProjectTypeIds?.length
    ? types.filter((type) => selectedProjectTypeIds.includes(type.value))
    : types

  const seenIds = new Set<string>()
  const milestones: FinanceMilestoneFilterOption[] = []

  for (const type of relevantTypes) {
    for (const milestone of type.milestones ?? []) {
      if (seenIds.has(milestone.value)) continue
      seenIds.add(milestone.value)
      milestones.push(milestone)
    }
  }

  return milestones
}

// Narrowing the project type filter can leave a previously-selected milestone
// out of scope (it belonged only to a project type that is no longer picked).
// Rather than keep filtering by a milestone id the dropdown no longer shows,
// drop it from the selection; an empty result normalizes to null so it reads
// the same as "no milestone filter" everywhere else in the filters object.
export const pruneSelectedMilestones = (
  selectedMilestoneIds: string[] | null | undefined,
  availableMilestones: FinanceMilestoneFilterOption[]
): string[] | null => {
  if (!selectedMilestoneIds?.length) return null

  const availableIds = new Set(availableMilestones.map((m) => m.value))
  const pruned = selectedMilestoneIds.filter((id) => availableIds.has(id))

  return pruned.length ? pruned : null
}
